import React, { useEffect, useState } from 'react';
import { SearchIcon, HeadphonesIcon, ChevronRightIcon } from '../components/icons';
import { LIBRARY_ARTICLES, LIBRARY_CATEGORIES, TINTS } from '../data/mock';
import { FullScreenModal } from '../components/FullScreenModal';
import { ArticleModal } from '../components/modals/ArticleModal';
type LibraryView = 'list' | 'article';
/**
 * Health Library — full-screen, in-navigation flow (no bottom modal).
 *
 *   list (articles) → article (the reader)
 *
 * The article reader is a layer in the SAME stack: ← returns to the list,
 * × closes the whole library back to the dashboard. Built on the reusable
 * FullScreenModal pattern.
 */
export function LibraryScreen({
  open,
  onClose,
  startAt = 'list'
}: {
  open: boolean;
  onClose: () => void;
  startAt?: LibraryView;
}) {
  const [view, setView] = useState<LibraryView>(startAt);
  const [category, setCategory] = useState('All');
  // re-seed the entry view each time the library opens
  useEffect(() => {
    if (open) setView(startAt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const articles = category === 'All' ? LIBRARY_ARTICLES : LIBRARY_ARTICLES.filter(a => a.category.toLowerCase().includes(category.toLowerCase()));
  return <FullScreenModal open={open} onClose={onClose} onBack={view === 'article' ? () => setView('list') : undefined} direction="up" eyebrow={view === 'list' ? 'Curated for you' : undefined} title={view === 'list' ? 'Library' : undefined} padded={false} resetOnOpen={false} data-id="element-980">
      {view === 'list' && <div className="dhp-fade px-5 pt-1" data-id="element-981">
          {/* search — the same semantic search the assistant uses */}
          <div className="flex items-center gap-2.5 rounded-full border border-linen-deep bg-cream px-4 py-3.5 shadow-card" data-id="element-982">
            <SearchIcon size={17} className="text-ink-faint" data-id="element-983" />
            <span className="text-[13.5px] font-medium text-ink-faint" data-id="element-984">
              Try “how do inhalers work?”
            </span>
          </div>

          {/* categories */}
          <div className="no-scrollbar mt-3.5 flex gap-2 overflow-x-auto" data-id="element-985">
            {LIBRARY_CATEGORIES.map(cat => <button key={cat} onClick={() => setCategory(cat)} className={`shrink-0 rounded-full px-4 py-2 text-[12.5px] font-bold transition-all ${category === cat ? 'bg-teal text-cream shadow-card' : 'bg-cream text-ink-soft hover:bg-linen-deep'}`} data-id="element-986">
                {cat}
              </button>)}
          </div>

          {/* article cards */}
          <section className="dhp-stagger mt-5 flex flex-col gap-2.5" data-id="element-987">
            {articles.map(article => {
          const tint = TINTS[article.tint];
          return <button key={article.id} onClick={() => setView('article')} className={`flex items-center gap-3.5 rounded-card border ${tint.border} bg-cream p-4 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]`} data-id="element-988">
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.soft}`} data-id="element-989">
                    <span className={`font-display text-[18px] font-semibold ${tint.text}`} data-id="element-990">
                      {article.title.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-991">
                    <p className={`text-[10px] font-bold uppercase tracking-[0.14em] ${tint.text}`} data-id="element-992">
                      {article.category}
                    </p>
                    <h3 className="font-display mt-0.5 text-[15px] font-semibold leading-snug text-ink" data-id="element-993">
                      {article.title}
                    </h3>
                    <p className="mt-1 flex items-center gap-2 text-[11.5px] font-semibold text-ink-faint" data-id="element-994">
                      {article.minutes} min read
                      {article.narrated && <span className="flex items-center gap-1 text-teal" data-id="element-995">
                          <HeadphonesIcon size={12} data-id="element-996" />
                          narrated
                        </span>}
                    </p>
                  </div>
                  <ChevronRightIcon size={16} className="shrink-0 text-ink-faint" data-id="element-997" />
                </button>;
        })}
          </section>
        </div>}

      {view === 'article' && <div className="dhp-fade" data-id="element-998">
          <ArticleModal key="library-article" data-id="element-999" />
        </div>}
    </FullScreenModal>;
}